/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import type { SearchSuggestionItem } from '../../api/search.types';

const STORAGE_KEY = 'r1s:exploreDestinations:recentLocations';
const MAX_RECENT = 2;

const REQUIRED_KEYS: (keyof SearchSuggestionItem)[] = [
    'id',
    'text',
    'lat',
    'lng',
    'icon',
    'type',
];

/**
 * Validates that a value from storage has the shape of SearchSuggestionItem.
 */
function isValidItem(value: unknown): value is SearchSuggestionItem {
    if (value === null || typeof value !== 'object') {
        return false;
    }
    const obj = value as Record<string, unknown>;
    return REQUIRED_KEYS.every((key) => typeof obj[key] === 'string');
}

/**
 * Reads recent location searches from sessionStorage.
 * Returns at most MAX_RECENT valid items, newest first.
 */
function readFromStorage(): SearchSuggestionItem[] {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return [];

        const parsed: unknown = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];

        const valid = parsed.filter(isValidItem);
        return valid.slice(0, MAX_RECENT);
    } catch {
        return [];
    }
}

/**
 * Writes recent location searches to sessionStorage.
 * Silently no-ops if storage is full or unavailable.
 */
function writeToStorage(items: SearchSuggestionItem[]): void {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
        // sessionStorage may be full or unavailable; silently ignore
    }
}

export function useRecentLocationSearches(): {
    recentSearches: SearchSuggestionItem[];
    addRecentSearch: (item: SearchSuggestionItem) => void;
} {
    const [recentSearches, setRecentSearches] =
        React.useState<SearchSuggestionItem[]>(readFromStorage);
    const recentSearchesRef = React.useRef(recentSearches);
    recentSearchesRef.current = recentSearches;

    const addRecentSearch = React.useCallback((item: SearchSuggestionItem) => {
        if (item.id === 'nearby') return;
        const prev = recentSearchesRef.current;
        const withoutThis = prev.filter((existing) => existing.id !== item.id);
        const next = [item, ...withoutThis].slice(0, MAX_RECENT);
        writeToStorage(next);
        setRecentSearches(next);
    }, []);

    return { recentSearches, addRecentSearch };
}
